import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, LockOutlined as LockIcon } from '@mui/icons-material';
import useOrderNotes, { NOTE_MAX } from './useOrderNotes';

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '');

function NoteItem({ note, saving, onEdit, onDelete }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const edited = note.updatedAt && note.createdAt && note.updatedAt !== note.createdAt;

  const save = async () => {
    if (!draft.trim()) return;
    if (await onEdit(note.id, draft.trim())) setEditing(false);
  };

  return (
    <Box data-testid="order-note" sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {note.authorName} · {when(note.createdAt)}{edited ? ` · ${t('orders.notes.edited')}` : ''}
        </Typography>
        {!editing && (
          <Box>
            <IconButton
              size="small" aria-label={t('common.edit')} disabled={saving}
              onClick={() => { setDraft(note.body); setEditing(true); }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small" aria-label={t('common.delete')} disabled={saving}
              onClick={() => { if (window.confirm(t('orders.notes.deleteConfirm'))) onDelete(note.id); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Stack>
      {editing ? (
        <Box>
          <TextField
            fullWidth multiline minRows={2} size="small" value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, NOTE_MAX))}
            onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') save(); }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setEditing(false)} disabled={saving}>{t('common.cancel')}</Button>
            <Button size="small" variant="contained" onClick={save} disabled={saving || !draft.trim()}>{t('common.save')}</Button>
          </Stack>
        </Box>
      ) : (
        <Typography variant="body2" data-testid="order-note-body" sx={{ whiteSpace: 'pre-wrap' }}>{note.body}</Typography>
      )}
    </Box>
  );
}

/** Props: orderId, onChanged() (the page refreshes its summary badges), autoFocus (boolean). */
export default function OrderNotesPanel({ orderId, onChanged, autoFocus = false }) {
  const { t } = useTranslation();
  const n = useOrderNotes(orderId, onChanged);
  const [draft, setDraft] = useState('');

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    if (await n.add(body)) setDraft('');
  };

  return (
    <Box data-testid="order-notes-panel">
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
        <LockIcon fontSize="inherit" color="action" />
        <Typography variant="caption" color="text.secondary">{t('orders.notes.private')}</Typography>
      </Stack>

      <TextField
        fullWidth multiline minRows={2} size="small" autoFocus={autoFocus}
        placeholder={t('orders.notes.placeholder')} value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, NOTE_MAX))}
        onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') submit(); }}
        helperText={t('orders.notes.counter', { used: draft.length, max: NOTE_MAX })}
      />
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1, mb: 2 }}>
        <Button variant="contained" size="small" onClick={submit} disabled={n.saving || !draft.trim()}>
          {t('orders.notes.add')}
        </Button>
      </Stack>

      {n.saveError && (
        <Alert severity="error" sx={{ mb: 1 }}>{t(`orders.notes.${n.saveError}`, { defaultValue: n.saveError })}</Alert>
      )}

      {n.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
      ) : n.loadError ? (
        <Alert severity="error" action={<Button size="small" onClick={n.reload}>{t('common.retry')}</Button>}>
          {t('orders.notes.loadFailed')}
        </Alert>
      ) : n.notes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{t('orders.notes.empty')}</Typography>
      ) : (
        <Stack spacing={1}>
          {n.notes.map((note) => (
            <NoteItem key={note.id} note={note} saving={n.saving} onEdit={n.edit} onDelete={n.remove} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
